// pages/formularios/PreviaFlow.js — o formulário como o cliente vê no WhatsApp.
//
// Out/26. Desenha UMA tela da definição no formato do Flow da Meta: barra
// com ✕, título e ⋮; os blocos; o botão verde do rodapé e o "Gerenciado
// pela empresa". Componente de apresentação puro: o mesmo desenho serve à
// prévia ao vivo do construtor e às miniaturas dos modelos (`compacta`),
// pra a pessoa reconhecer o formulário pelo desenho antes de ler o nome.

import { useLayoutEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import {
  FiCalendar, FiCamera, FiChevronDown, FiChevronLeft, FiChevronRight,
  FiFileText, FiMoreVertical, FiX,
} from 'react-icons/fi';
import { ESCOLHAS } from './regras';

const ink = 'var(--fm-wa-ink)';
const ink2 = 'var(--fm-wa-ink2)';
const linha = 'var(--fm-wa-line)';

function Campo({ children, alto, icone }) {
  return (
    <Box sx={{
      border: `1px solid ${linha}`, borderRadius: '8px', px: 1.5,
      minHeight: alto ? 72 : 44, display: 'flex', alignItems: alto ? 'flex-start' : 'center',
      pt: alto ? 1.2 : 0, gap: 1, color: 'var(--fm-wa-field)', fontSize: '0.85rem',
    }}>
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{children}</Box>
      {icone}
    </Box>
  );
}

function opcional(b) {
  return b.obrigatorio ? '' : ' (opcional)';
}

function Bloco({ b }) {
  const rotulo = (b.rotulo || '').trim() || 'Sem rótulo';
  switch (b.tipo) {
    case 'conteudo': {
      const texto = (b.texto || '').trim() || 'Texto';
      const s = {
        titulo: { fontSize: '1.05rem', fontWeight: 700, color: ink },
        subtitulo: { fontSize: '0.92rem', fontWeight: 700, color: ink },
        paragrafo: { fontSize: '0.84rem', color: ink, whiteSpace: 'pre-wrap' },
        legenda: { fontSize: '0.74rem', color: ink2, whiteSpace: 'pre-wrap' },
      }[b.estilo || 'paragrafo'];
      return <Typography sx={{ ...s, lineHeight: 1.35, wordBreak: 'break-word' }}>{texto}</Typography>;
    }
    case 'imagem':
      return b.src ? (
        <Box component="img" src={b.src} alt={b.descricao || ''} sx={{ width: '100%', maxHeight: 150, objectFit: 'contain', borderRadius: '8px', display: 'block' }} />
      ) : (
        <Box sx={{ height: 90, borderRadius: '8px', border: `1px dashed ${linha}`, display: 'grid', placeItems: 'center', color: ink2, fontSize: '0.75rem' }}>Imagem</Box>
      );
    case 'area':
      return (
        <Box>
          <Campo alto>{rotulo}{opcional(b)}</Campo>
          <Typography sx={{ textAlign: 'right', fontSize: '0.66rem', color: ink2, mt: 0.4 }}>0 / 600</Typography>
        </Box>
      );
    case 'data':
    case 'calendario':
      return <Campo icone={<FiCalendar size={15} />}>{rotulo}{opcional(b)}</Campo>;
    case 'lista':
      return <Campo icone={<FiChevronDown size={16} />}>{rotulo}{opcional(b)}</Campo>;
    case 'aceite':
      return (
        <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'flex-start' }}>
          <Box sx={{ width: 18, height: 18, border: `2px solid ${ink2}`, borderRadius: '3px', flexShrink: 0, mt: '1px' }} />
          <Typography sx={{ fontSize: '0.82rem', color: ink, lineHeight: 1.35 }}>{rotulo}{opcional(b)}</Typography>
        </Box>
      );
    case 'foto':
    case 'documento':
      return (
        <Box>
          <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: ink }}>{rotulo}{opcional(b)}</Typography>
          {(b.descricao || '').trim() && <Typography sx={{ fontSize: '0.74rem', color: ink2, mt: 0.3 }}>{b.descricao}</Typography>}
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, color: 'var(--fm-green-t)', fontSize: '0.82rem', fontWeight: 700 }}>
            {b.tipo === 'foto' ? <FiCamera size={15} /> : <FiFileText size={15} />}
            {b.tipo === 'foto' ? 'Tirar ou escolher foto' : 'Escolher documento'}
          </Box>
        </Box>
      );
    default:
      if (ESCOLHAS.includes(b.tipo)) {
        const multipla = b.tipo === 'multipla';
        const ops = (b.opcoes || []).filter((o) => String(o).trim());
        return (
          <Box>
            <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: ink, lineHeight: 1.3 }}>{rotulo}</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: ink2, mt: 0.6, mb: 0.4, fontWeight: 600 }}>
              {multipla ? 'Escolha uma ou mais' : 'Escolha uma'}{b.obrigatorio ? '' : ' (opcional)'}
            </Typography>
            {(ops.length ? ops : ['Opção 1', 'Opção 2']).map((op, k) => (
              <Box key={k} sx={{ display: 'flex', alignItems: 'center', py: 0.9, gap: 1 }}>
                <Typography sx={{ flex: 1, fontSize: '0.84rem', color: ink, minWidth: 0, wordBreak: 'break-word' }}>{op}</Typography>
                <Box sx={{ width: 17, height: 17, border: `2px solid ${ink2}`, borderRadius: multipla ? '3px' : '50%', flexShrink: 0 }} />
              </Box>
            ))}
          </Box>
        );
      }
      return <Campo>{rotulo}{opcional(b)}</Campo>;
  }
}

// Miniatura: o desenho inteiro numa largura fixa (300), reduzido até caber
// na largura do cartão. Não é uma segunda implementação da prévia — é a
// mesma, encolhida. A escala acompanha o cartão (ResizeObserver): com escala
// fixa, no celular a miniatura ficava torta num canto do cartão largo e, no
// desktop, cortada na borda.
function Miniatura({ parede, children }) {
  const caixa = useRef(null);
  const [escala, setEscala] = useState(0.5);
  useLayoutEffect(() => {
    const el = caixa.current;
    if (!el) return undefined;
    const medir = () => setEscala(Math.max(0.3, el.clientWidth / 300));
    medir();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <Box ref={caixa} aria-hidden sx={{ height: 150, overflow: 'hidden', borderRadius: '10px', position: 'relative' }}>
      <Box sx={{ width: 300, transform: `scale(${escala})`, transformOrigin: 'top left', pointerEvents: 'none', ...parede }}>
        {children}
      </Box>
    </Box>
  );
}

/**
 * @param definicao   {telas:[…]}
 * @param indice      tela mostrada
 * @param onTrocar    (novoIndice) => void — sem ele, não há paginação
 * @param compacta    miniatura (sem paginação, sem interação)
 */
export default function PreviaFlow({ definicao, indice = 0, onTrocar, compacta = false }) {
  const telas = definicao?.telas || [];
  const tela = telas[Math.min(indice, Math.max(0, telas.length - 1))] || { titulo: '', rodape: '', blocos: [] };
  const total = telas.length || 1;

  const cartao = (
    <Box sx={{
      bgcolor: 'var(--fm-wa-card)', borderRadius: '14px', overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column',
      height: compacta ? 330 : 380,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1.3, borderBottom: `1px solid ${linha}`, color: ink }}>
        <FiX size={17} />
        <Typography sx={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: '0.92rem', color: ink, px: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {(tela.titulo || '').trim() || 'Sem título'}
        </Typography>
        <FiMoreVertical size={16} />
      </Box>
      <Box sx={{ flex: 1, overflowY: compacta ? 'hidden' : 'auto', px: 1.75, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.4 }}>
        {(tela.blocos || []).map((b) => <Bloco key={b.id} b={b} />)}
      </Box>
      <Box sx={{ borderTop: `1px solid ${linha}`, px: 1.75, pt: 1.2, pb: 0.9 }}>
        <Box sx={{
          bgcolor: 'var(--fm-green)', color: 'var(--fm-on-green)', borderRadius: '999px', textAlign: 'center',
          py: 0.95, fontWeight: 700, fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {(tela.rodape || '').trim() || 'Continuar'}
        </Box>
        <Typography sx={{ textAlign: 'center', fontSize: '0.64rem', color: ink2, mt: 0.8 }}>
          Gerenciado pela empresa · Saiba mais
        </Typography>
      </Box>
    </Box>
  );

  const parede = {
    p: compacta ? 1.25 : 2.25, borderRadius: '16px', bgcolor: 'var(--fm-wa-wall)',
    backgroundImage: 'radial-gradient(var(--fm-wa-dot) 1px, transparent 1px)', backgroundSize: '14px 14px',
  };

  if (compacta) {
    return <Miniatura parede={parede}>{cartao}</Miniatura>;
  }

  return (
    <Box>
      <Box sx={parede}>{cartao}</Box>
      {onTrocar && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1.2 }}>
          <IconButton size="small" aria-label="Tela anterior" disabled={indice <= 0} onClick={() => onTrocar(indice - 1)} sx={{ color: 'var(--fm-ink2)' }}>
            <FiChevronLeft size={16} />
          </IconButton>
          <Typography sx={{ fontSize: '0.78rem', color: 'var(--fm-body)', minWidth: 80, textAlign: 'center' }}>
            Tela {Math.min(indice + 1, total)} de {total}
          </Typography>
          <IconButton size="small" aria-label="Próxima tela" disabled={indice >= total - 1} onClick={() => onTrocar(indice + 1)} sx={{ color: 'var(--fm-ink2)' }}>
            <FiChevronRight size={16} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}
